import { memo, useMemo } from 'react';
import { cn } from '@divlab/divanui';

import Button from '@UI/Button';
import useMedias from '@Hooks/useMedias';
import useTranslation from '@Queries/useTranslation';
import WikiArticle from '@Components/WikiArticle';
import styles from './WikiArticlesCatalog.module.css';

import type { WikiArticleData } from '@Types/Wiki';
import type { FC, HTMLAttributes, MouseEvent } from 'react';

export interface PagesDate {
  page: number;
  articles: WikiArticleData[];
  countLeft: number;
}

export interface WikiArticlesCatalogProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  pages: PagesDate[];
  waiting?: boolean;
  hasNextPage?: boolean;
  onMore?: (e: MouseEvent) => void;
}

const WikiArticlesCatalog: FC<WikiArticlesCatalogProps> = (props) => {
  const { className, pages, waiting, hasNextPage, onMore, ...restProps } = props;
  const { isDesktop } = useMedias();
  const { t } = useTranslation();
  const startArticlesCount = isDesktop ? 6 : 8;

  const articles = useMemo(() => {
    const allArticles = [];

    if (pages.length === 1) {
      pages[0].articles.forEach((article) => {
        allArticles.push(article);
      });

      return allArticles.slice(0, startArticlesCount);
    }

    pages.forEach((page) => {
      page.articles.forEach((article) => {
        allArticles.push(article);
      });
    });

    return allArticles;
  }, [pages, startArticlesCount]);

  return (
    <div {...restProps} className={cn(styles.catalog, className)}>
      <div className={styles.list}>
        {articles.map((article, index) => (
          <div className={styles.item} key={index}>
            <WikiArticle article={article} />
          </div>
        ))}
      </div>

      {hasNextPage && (
        <div className={styles.moreWrapper}>
          <Button className={styles.moreButton} theme='dirty' waiting={waiting} onClick={onMore}>
            {t('ui.watch-more')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default memo(WikiArticlesCatalog);
